import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Plus, X, QrCode, Download, Share2, Phone, Mail, AlertTriangle } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { API } from '../App';
import { toast, Toaster } from 'sonner';
import QRCode from 'qrcode';

const EmergencyProfile = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [generatingQR, setGeneratingQR] = useState(false);
  
  const [formData, setFormData] = useState({
    blood_type: '',
    allergies: [''],
    chronic_conditions: [''],
    current_medications: [''],
    emergency_contacts: [{ name: '', relationship: '', phone: '', email: '' }],
    additional_notes: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/emergency-profile`);
      if (response.data.profile) {
        setProfile(response.data.profile);
        setFormData({
          blood_type: response.data.profile.blood_type || '',
          allergies: response.data.profile.allergies.length > 0 ? response.data.profile.allergies : [''],
          chronic_conditions: response.data.profile.chronic_conditions.length > 0 ? response.data.profile.chronic_conditions : [''],
          current_medications: response.data.profile.current_medications.length > 0 ? response.data.profile.current_medications : [''],
          emergency_contacts: response.data.profile.emergency_contacts.length > 0 ? response.data.profile.emergency_contacts : [{ name: '', relationship: '', phone: '', email: '' }],
          additional_notes: response.data.profile.additional_notes || ''
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Filter out empty entries
      const cleanedData = {
        ...formData,
        allergies: formData.allergies.filter(a => a.trim()),
        chronic_conditions: formData.chronic_conditions.filter(c => c.trim()),
        current_medications: formData.current_medications.filter(m => m.trim()),
        emergency_contacts: formData.emergency_contacts.filter(c => c.name.trim() && c.phone.trim())
      };

      const response = await axios.post(`${API}/emergency-profile`, cleanedData);
      toast.success(t('emergency.profileSaved'));
      
      // Set profile with ID
      const updatedProfile = { ...cleanedData, id: response.data.profile_id, qr_code_url: response.data.qr_code_url };
      setProfile(updatedProfile);
      
      // Auto-generate QR code after saving
      setTimeout(() => {
        if (response.data.profile_id) {
          const emergencyUrl = `${window.location.origin}/emergency-view/${response.data.profile_id}`;
          QRCode.toDataURL(emergencyUrl, {
            width: 400,
            margin: 2,
            color: {
              dark: '#0D9488',
              light: '#FFFFFF'
            }
          }).then(qrDataUrl => {
            setQrCodeUrl(qrDataUrl);
            toast.success('QR Code generated! Click "View QR Code" to see it.');
          }).catch(err => {
            console.error('QR generation error:', err);
          });
        }
      }, 500);
    } catch (error) {
      toast.error(t('emergency.saveFailed'));
      console.error('Save error:', error);
    }
  };

  const addItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeItem = (field, index) => {
    const newItems = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newItems });
  };

  const updateItem = (field, index, value) => {
    const newItems = [...formData[field]];
    newItems[index] = value;
    setFormData({ ...formData, [field]: newItems });
  };

  const addContact = () => {
    setFormData({
      ...formData,
      emergency_contacts: [...formData.emergency_contacts, { name: '', relationship: '', phone: '', email: '' }]
    });
  };

  const removeContact = (index) => {
    const newContacts = formData.emergency_contacts.filter((_, i) => i !== index);
    setFormData({ ...formData, emergency_contacts: newContacts });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...formData.emergency_contacts];
    newContacts[index][field] = value;
    setFormData({ ...formData, emergency_contacts: newContacts });
  };

  const generateQRCode = async () => {
    if (!profile || !profile.id) {
      console.error('Cannot generate QR: profile or profile.id is missing', { profile });
      toast.error('Please save your profile first before generating QR code');
      return;
    }

    const emergencyUrl = `${window.location.origin}/emergency-view/${profile.id}`;
    process.env.NODE_ENV === 'development' && console.log('Generating QR for URL:', emergencyUrl);
    
    setGeneratingQR(true);
    
    try {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      
      // Generate QR code on canvas
      await QRCode.toCanvas(canvas, emergencyUrl, {
        width: 400,
        margin: 2,
        color: {
          dark: '#0D9488',
          light: '#FFFFFF'
        }
      });
      
      // Convert canvas to data URL
      const qrDataUrl = canvas.toDataURL('image/png');
      
      process.env.NODE_ENV === 'development' && console.log('QR Code generated successfully, length:', qrDataUrl.length);
      process.env.NODE_ENV === 'development' && console.log('QR Data URL starts with:', qrDataUrl.substring(0, 50));
      
      if (!qrDataUrl || qrDataUrl.length < 100) {
        throw new Error('Generated QR code is invalid or empty');
      }
      
      setQrCodeUrl(qrDataUrl);
      setShowQR(true);
      toast.success('QR Code generated!');
    } catch (error) {
      console.error('QR generation error:', error);
      console.error('Error details:', error.stack);
      toast.error('Failed to generate QR code: ' + error.message);
    } finally {
      setGeneratingQR(false);
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.download = 'emergency-health-profile-qr.png';
    link.href = qrCodeUrl;
    link.click();
  };

  const shareProfile = async () => {
    if (!profile || !profile.id) {
      toast.error('Please save your profile first before sharing');
      return;
    }
    
    const emergencyUrl = `${window.location.origin}/emergency-view/${profile.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Emergency Health Profile',
          text: 'Access my emergency health information',
          url: emergencyUrl
        });
      } catch (error) {
        // User cancelled share or error occurred
        process.env.NODE_ENV === 'development' && console.log('Share cancelled or failed:', error);
      }
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(emergencyUrl);
      toast.success(t('emergency.linkCopied'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <Navigation user={user} setUser={setUser} />
      <Toaster position="top-right" richColors />

      <div className="container mx-auto px-4 py-6 sm:py-8 pt-24">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                <Shield className="w-10 h-10 text-red-600" />
                {t('emergency.title')}
              </h1>
              <p className="text-gray-600 mt-2">{t('emergency.subtitle')}</p>
            </div>
            {profile && (
              <div className="flex gap-2">
                <Button 
                  onClick={generateQRCode} 
                  disabled={generatingQR}
                  variant="outline" 
                  className="gap-2"
                >
                  {generatingQR ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                  ) : (
                    <QrCode className="w-4 h-4" />
                  )}
                  {generatingQR ? 'Generating...' : t('emergency.viewQR')}
                </Button>
                <Button onClick={shareProfile} variant="outline" className="gap-2">
                  <Share2 className="w-4 h-4" />
                  {t('emergency.share')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Alert Banner */}
        <Card className="p-4 mb-6 bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">{t('emergency.important')}</p>
              <p className="text-sm text-red-700 mt-1">{t('emergency.importantMessage')}</p>
            </div>
          </div>
        </Card>

        {/* Form */}
        <Card className="p-8 bg-white shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Blood Type */}
            <div>
              <Label htmlFor="blood_type">{t('emergency.bloodType')}</Label>
              <select
                id="blood_type"
                value={formData.blood_type}
                onChange={(e) => setFormData({ ...formData, blood_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-md mt-1"
              >
                <option value="">{t('emergency.selectBloodType')}</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>

            {/* Allergies */}
            <div>
              <Label>{t('emergency.allergies')}</Label>
              {formData.allergies.map((allergy, index) => (
                <div key={`item-${index}`} className="flex gap-2 mt-2">
                  <Input
                    value={allergy}
                    onChange={(e) => updateItem('allergies', index, e.target.value)}
                    placeholder={t('emergency.allergyPlaceholder')}
                  />
                  {formData.allergies.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeItem('allergies', index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => addItem('allergies')}
                className="w-full mt-2"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('emergency.addAllergy')}
              </Button>
            </div>

            {/* Chronic Conditions */}
            <div>
              <Label>{t('emergency.chronicConditions')}</Label>
              {formData.chronic_conditions.map((condition, index) => (
                <div key={`item-${index}`} className="flex gap-2 mt-2">
                  <Input
                    value={condition}
                    onChange={(e) => updateItem('chronic_conditions', index, e.target.value)}
                    placeholder={t('emergency.conditionPlaceholder')}
                  />
                  {formData.chronic_conditions.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeItem('chronic_conditions', index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => addItem('chronic_conditions')}
                className="w-full mt-2"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('emergency.addCondition')}
              </Button>
            </div>

            {/* Current Medications */}
            <div>
              <Label>{t('emergency.currentMedications')}</Label>
              {formData.current_medications.map((medication, index) => (
                <div key={`item-${index}`} className="flex gap-2 mt-2">
                  <Input
                    value={medication}
                    onChange={(e) => updateItem('current_medications', index, e.target.value)}
                    placeholder={t('emergency.medicationPlaceholder')}
                  />
                  {formData.current_medications.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeItem('current_medications', index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => addItem('current_medications')}
                className="w-full mt-2"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('emergency.addMedication')}
              </Button>
            </div>

            {/* Emergency Contacts */}
            <div>
              <Label className="text-lg font-semibold">{t('emergency.emergencyContacts')}</Label>
              {formData.emergency_contacts.map((contact, index) => (
                <Card key={`item-${index}`} className="p-4 mt-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-medium">{t('emergency.contact')} {index + 1}</span>
                    {formData.emergency_contacts.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeContact(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('emergency.contactName')}</Label>
                      <Input
                        value={contact.name}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <Label>{t('emergency.relationship')}</Label>
                      <Input
                        value={contact.relationship}
                        onChange={(e) => updateContact(index, 'relationship', e.target.value)}
                        placeholder={t('emergency.relationshipPlaceholder')}
                      />
                    </div>
                    <div>
                      <Label>{t('emergency.phone')}</Label>
                      <Input
                        value={contact.phone}
                        onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                    <div>
                      <Label>{t('emergency.email')}</Label>
                      <Input
                        type="email"
                        value={contact.email}
                        onChange={(e) => updateContact(index, 'email', e.target.value)}
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                </Card>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addContact}
                className="w-full mt-4"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('emergency.addContact')}
              </Button>
            </div>

            {/* Additional Notes */}
            <div>
              <Label htmlFor="additional_notes">{t('emergency.additionalNotes')}</Label>
              <textarea
                id="additional_notes"
                value={formData.additional_notes}
                onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                placeholder={t('emergency.notesPlaceholder')}
                className="w-full px-3 py-2 border rounded-md mt-1"
                rows="4"
              />
            </div>

            {/* Submit Button */}
            <Button type="submit" className="w-full py-6 text-lg bg-gradient-to-r from-red-600 to-orange-600">
              <Shield className="w-5 h-5 mr-2" />
              {t('emergency.saveProfile')}
            </Button>
          </form>
        </Card>

        {/* QR Code Dialog */}
        <Dialog open={showQR} onOpenChange={setShowQR}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('emergency.qrCodeTitle')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex justify-center bg-white p-4 rounded-lg border-2 border-gray-200">
                {generatingQR ? (
                  <div className="flex flex-col items-center justify-center w-64 h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
                    <p className="text-gray-600">Generating QR Code...</p>
                  </div>
                ) : qrCodeUrl ? (
                  <img 
                    src={qrCodeUrl} 
                    alt="Emergency Profile QR Code" 
                    className="w-64 h-64"
                    onError={(e) => {
                      console.error('QR image failed to load');
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-64 h-64 flex items-center justify-center text-gray-400">
                    <p>No QR Code generated</p>
                  </div>
                )}
              </div>
              <p className="text-sm text-center text-gray-600">
                {t('emergency.qrInstructions')}
              </p>
              <div className="flex gap-2">
                <Button onClick={downloadQRCode} disabled={!qrCodeUrl} className="flex-1">
                  <Download className="w-4 h-4 mr-2" />
                  {t('emergency.download')}
                </Button>
                <Button onClick={shareProfile} disabled={!qrCodeUrl} variant="outline" className="flex-1">
                  <Share2 className="w-4 h-4 mr-2" />
                  {t('emergency.share')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default EmergencyProfile;
